import { db } from "@/firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
  updateDoc,
} from "firebase/firestore";

function clean(s) {
  return String(s ?? "").trim();
}

function isISODate(s) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(s || "").trim());
}

function toISO(d) {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function timestampMillis(value) {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  return 0;
}

function slugifyStaffKey(value) {
  return clean(value)
    .toLowerCase()
    .replace(/[^a-z0-9-_]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function requestTypeLabelFromRequest(request) {
  if (request?.requestTypeLabel) return clean(request.requestTypeLabel);

  if (request?.requestType === "unpaid_leave" || request?.isUnpaidLeave === true) {
    return "Unpaid Leave";
  }

  if (request?.requestType === "paid_holiday" || request?.isPaidHoliday === true) {
    return "Paid Holiday";
  }

  return "Paid Holiday";
}

function requestBlockLabelFromRequest(request) {
  if (request?.requestType === "unpaid_leave" || request?.isUnpaidLeave === true) {
    return "Unpaid Leave";
  }

  return "Holiday";
}

function requestBlockTypeFromRequest(request) {
  if (request?.requestType === "unpaid_leave" || request?.isUnpaidLeave === true) {
    return "unpaid_leave";
  }

  return "holiday";
}

export function datesBetweenInclusive(startISO, endISO) {
  if (!isISODate(startISO) || !isISODate(endISO)) return [];
  if (endISO < startISO) return [];

  const out = [];
  const d = new Date(`${startISO}T00:00:00`);
  const end = new Date(`${endISO}T00:00:00`);

  while (d <= end) {
    out.push(toISO(d));
    d.setDate(d.getDate() + 1);
  }

  return out;
}

export async function createHolidayRequest({
  venueId,
  venueName,
  requesterUid,
  requesterEmail,
  staffId,
  staffName,
  staffRole,
  startDate,
  endDate,
  dates,
  requestType,
  requestTypeLabel,
  isPaidHoliday,
  isUnpaidLeave,
  notes,
}) {
  const cleanVenueId = clean(venueId);
  const cleanStaffId = clean(staffId);
  const cleanStaffName = clean(staffName);
  const cleanRequestType = clean(requestType);
  const cleanRequestTypeLabel =
    clean(requestTypeLabel) ||
    (cleanRequestType === "unpaid_leave" ? "Unpaid Leave" : "Paid Holiday");

  if (!cleanVenueId) throw new Error("Missing venueId.");
  if (!cleanStaffId && !cleanStaffName) throw new Error("Missing staff identity.");
  if (!isISODate(startDate) || !isISODate(endDate)) throw new Error("Invalid holiday dates.");

  if (cleanRequestType !== "paid_holiday" && cleanRequestType !== "unpaid_leave") {
    throw new Error("Please select Paid Holiday or Unpaid Leave.");
  }

  const requestDates =
    Array.isArray(dates) && dates.length
      ? dates.filter(isISODate)
      : datesBetweenInclusive(startDate, endDate);

  if (!requestDates.length) throw new Error("No valid holiday dates selected.");

  const payload = {
    venueId: cleanVenueId,
    venueName: clean(venueName),

    requesterUid: clean(requesterUid),
    requesterEmail: clean(requesterEmail).toLowerCase(),

    staffId: cleanStaffId,
    staffName: cleanStaffName,
    staffRole: clean(staffRole),

    startDate,
    endDate,
    dates: requestDates,
    totalDays: requestDates.length,

    requestType: cleanRequestType,
    requestTypeLabel: cleanRequestTypeLabel,
    isPaidHoliday: isPaidHoliday === true || cleanRequestType === "paid_holiday",
    isUnpaidLeave: isUnpaidLeave === true || cleanRequestType === "unpaid_leave",

    notes: clean(notes),

    status: "pending",
    submittedAt: serverTimestamp(),
    submittedByUid: clean(requesterUid),
    submittedByEmail: clean(requesterEmail).toLowerCase(),

    approvedAt: null,
    approvedByUid: "",
    approvedByEmail: "",
    approvedByDisplayName: "",

    rejectedAt: null,
    rejectedByUid: "",
    rejectedByEmail: "",
    rejectionReason: "",
  };

  const ref = await addDoc(
    collection(db, "venues", cleanVenueId, "holiday_requests"),
    payload
  );

  return {
    id: ref.id,
    ...payload,
  };
}

export async function fetchHolidayBlocksForDateRange({
  venueId,
  startDate,
  endDate,
}) {
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) return [];
  if (!isISODate(startDate) || !isISODate(endDate)) return [];

  const blocksRef = collection(db, "venues", cleanVenueId, "holiday_blocks");

  const q = query(
    blocksRef,
    where("date", ">=", startDate),
    where("date", "<=", endDate)
  );

  const snap = await getDocs(q);

  return snap.docs
    .map((d) => ({
      id: d.id,
      ...(d.data() || {}),
    }))
    .sort((a, b) => {
      const dateCompare = String(a.date || "").localeCompare(String(b.date || ""));
      if (dateCompare !== 0) return dateCompare;
      return String(a.staffName || "").localeCompare(String(b.staffName || ""));
    });
}

export async function fetchPendingHolidayRequests({ venueId }) {
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) return [];

  const requestsRef = collection(db, "venues", cleanVenueId, "holiday_requests");

  const q = query(
    requestsRef,
    where("status", "==", "pending")
  );

  const snap = await getDocs(q);

  return snap.docs
    .map((d) => ({
      id: d.id,
      ...(d.data() || {}),
    }))
    .sort((a, b) => {
      const submittedCompare = timestampMillis(a.submittedAt) - timestampMillis(b.submittedAt);
      if (submittedCompare !== 0) return submittedCompare;

      const startCompare = String(a.startDate || "").localeCompare(String(b.startDate || ""));
      if (startCompare !== 0) return startCompare;

      return String(a.staffName || "").localeCompare(String(b.staffName || ""));
    });
}

export async function approveHolidayRequest({
  venueId,
  request,
  approverUid,
  approverEmail,
  approverDisplayName,
}) {
  const cleanVenueId = clean(venueId);
  const cleanApproverUid = clean(approverUid);
  const cleanApproverEmail = clean(approverEmail).toLowerCase();
  const cleanApproverDisplayName =
    clean(approverDisplayName) ||
    cleanApproverEmail ||
    cleanApproverUid;

  if (!cleanVenueId) throw new Error("Missing venueId.");
  if (!request?.id) throw new Error("Missing request id.");

  const dates =
    Array.isArray(request.dates) && request.dates.length
      ? request.dates.filter(isISODate)
      : datesBetweenInclusive(request.startDate, request.endDate);

  if (!dates.length) throw new Error("Request has no valid dates.");

  const blockLabel = requestBlockLabelFromRequest(request);
  const blockType = requestBlockTypeFromRequest(request);
  const resolvedRequestType =
    clean(request.requestType) ||
    (blockType === "unpaid_leave" ? "unpaid_leave" : "paid_holiday");

  const resolvedRequestTypeLabel = requestTypeLabelFromRequest({
    ...request,
    requestType: resolvedRequestType,
  });

  const batch = writeBatch(db);

  const requestRef = doc(
    db,
    "venues",
    cleanVenueId,
    "holiday_requests",
    request.id
  );

  batch.update(requestRef, {
    status: "approved",
    approvedAt: serverTimestamp(),
    approvedByUid: cleanApproverUid,
    approvedByEmail: cleanApproverEmail,
    approvedByDisplayName: cleanApproverDisplayName,
    approvedDates: dates,

    requestType: resolvedRequestType,
    requestTypeLabel: resolvedRequestTypeLabel,
    isPaidHoliday: resolvedRequestType === "paid_holiday",
    isUnpaidLeave: resolvedRequestType === "unpaid_leave",

    rejectedAt: null,
    rejectedByUid: "",
    rejectedByEmail: "",
    rejectionReason: "",
  });

  for (const date of dates) {
    const staffKey =
      clean(request.staffId) ||
      slugifyStaffKey(request.staffName) ||
      slugifyStaffKey(request.requesterEmail);

    if (!staffKey) {
      throw new Error("Cannot approve holiday request because staff identity is missing.");
    }

    const blockId = `${staffKey}_${date}`;

    const blockRef = doc(
      db,
      "venues",
      cleanVenueId,
      "holiday_blocks",
      blockId
    );

    batch.set(
      blockRef,
      {
        requestId: request.id,
        venueId: cleanVenueId,

        staffId: clean(request.staffId),
        staffName: clean(request.staffName),
        staffRole: clean(request.staffRole),

        date,
        label: blockLabel,
        type: blockType,

        requestType: resolvedRequestType,
        requestTypeLabel: resolvedRequestTypeLabel,
        isPaidHoliday: resolvedRequestType === "paid_holiday",
        isUnpaidLeave: resolvedRequestType === "unpaid_leave",

        status: "approved",

        approvedAt: serverTimestamp(),
        approvedByUid: cleanApproverUid,
        approvedByEmail: cleanApproverEmail,
        approvedByDisplayName: cleanApproverDisplayName,

        startDate: request.startDate || dates[0],
        endDate: request.endDate || dates[dates.length - 1],
      },
      { merge: true }
    );
  }

  await batch.commit();

  return true;
}

export async function rejectHolidayRequest({
  venueId,
  requestId,
  approverUid,
  approverEmail,
  reason,
}) {
  const cleanVenueId = clean(venueId);

  if (!cleanVenueId) throw new Error("Missing venueId.");
  if (!requestId) throw new Error("Missing request id.");

  const requestRef = doc(
    db,
    "venues",
    cleanVenueId,
    "holiday_requests",
    requestId
  );

  await updateDoc(requestRef, {
    status: "rejected",
    rejectedAt: serverTimestamp(),
    rejectedByUid: clean(approverUid),
    rejectedByEmail: clean(approverEmail).toLowerCase(),
    rejectionReason: clean(reason),
  });

  return true;
}